// Campaign Submitted confirmation page for InfluenceHub MVP
// Shows confirmation message after successful campaign creation
// Provides next steps and navigation options

import React from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import Navbar from '../../components/layout/Navbar';
import Sidebar from '../../components/layout/Sidebar';

const CampaignSubmitted = ({ onNavigate }) => {
  const { profile } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation bar */}
      <Navbar onNavigate={onNavigate} />
      
      <div className="flex">
        {/* Sidebar navigation */}
        <Sidebar onNavigate={onNavigate} />
        
        {/* Main content area */}
        <main className="flex-1 p-6">
          <div className="max-w-4xl mx-auto">
            {/* Success message */}
            <div className="text-center py-12">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Campaign Submitted Successfully!
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Your campaign has been submitted and is awaiting review. Our team will confirm your campaign shortly inside your dashboard.
              </p>

              {/* Status card */}
              <div className="bg-white rounded-lg shadow p-6 mb-8 max-w-md mx-auto">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-900">Status: Pending Approval</span>
                </div>
                <p className="text-sm text-gray-600">
                  You'll see updates in your dashboard once it's approved.
                </p>
              </div>

              {/* Next steps */}
              <div className="bg-blue-50 rounded-lg p-6 mb-8 max-w-2xl mx-auto">
                <h2 className="text-lg font-semibold text-blue-900 mb-4">What happens next?</h2>
                <div className="space-y-3 text-left">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-blue-600">1</span>
                    </div>
                    <p className="text-sm text-blue-800">
                      Our team will review your campaign details and approve it shortly.
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-blue-600">2</span>
                    </div>
                    <p className="text-sm text-blue-800">
                      Once approved, your campaign will appear as "Active" in your dashboard.
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-blue-600">3</span>
                    </div>
                    <p className="text-sm text-blue-800">
                      You can then start receiving influencer applications through InfluenceHub.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => onNavigate('dashboard')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  Back to Dashboard
                </button>
                <button
                  onClick={() => onNavigate('create-campaign')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  Create Another Campaign
                </button>
              </div>

              {/* Contact info */}
              <div className="mt-12 text-center">
                <p className="text-sm text-gray-500">
                  Questions? Contact us at{' '}
                  <a href="mailto:hello@influencehub.net" className="text-blue-600 hover:text-blue-500">
                    hello@influencehub.net
                  </a>
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CampaignSubmitted;
